import firestoreService from "../services/firestoreService";
import {
  MIL882EMatrix,
  SampleMatrix,
  calculateRiskRating as calculateRating,
  getContrastColor,
  stringToMatrix,
} from "../components/Risk Matrix/riskMatrixShared";

// Export standard matrices directly from shared module
export { MIL882EMatrix, SampleMatrix, getContrastColor };

export const riskStatusColors = {
  Open: "bg-blue-200 text-blue-800 border-l-4 border-blue-500",
  Managed: "bg-green-200 text-green-800 border-l-4 border-green-500",
  Resolved: "bg-purple-200 text-purple-800 border-l-4 border-purple-500",
  "For Transfer": "bg-orange-200 text-orange-800 border-l-4 border-orange-500",
  Transferred: "bg-indigo-200 text-indigo-800 border-l-4 border-indigo-500",
  Closed: "bg-gray-200 text-gray-800 border-l-4 border-gray-500",
  Cancelled:
    "bg-gray-300 text-gray-800 border-l-4 border-gray-500 line-through",
};

// Helper function to get matrix by id or name
export const getMatrix = async (matrixIdOrName) => {
  // First check if it's a preset matrix
  if (matrixIdOrName === MIL882EMatrix.name) {
    return MIL882EMatrix;
  }

  if (matrixIdOrName === SampleMatrix.name) {
    return SampleMatrix;
  }

  // If not preset, try to fetch custom matrix
  try {
    // Use get method instead of non-existent getRiskMatrix
    const matrix = await firestoreService.get("riskHelper", matrixIdOrName);

    // Ensure matrix is in array format if it comes from Firestore
    if (matrix) {
      return {
        ...matrix,
        matrix: typeof matrix.matrix === "string"
            ? stringToMatrix(matrix.matrix)
            : matrix.matrix,
        descriptions: matrix.descriptions || {
          probabilities: {},
          severities: {},
        },
        diagonalText: matrix.diagonalText || {
          top: "Severity",
          bottom: "Probability",
        },
      };
    }
    
    // If matrix is not in Firestore but matches a known matrix name,
    // fall back to local definitions
    if (matrixIdOrName === "Sample Matrix") {
      console.log("Using local Sample Matrix definition instead of Firestore");
      return SampleMatrix;
    }
    
    return null;
  } catch (error) {
    console.error("Error fetching risk matrix:", error);
    
    // Additional fallback for Sample Matrix in case of error
    if (matrixIdOrName === "Sample Matrix") {
      console.log("Error occurred, using local Sample Matrix definition");
      return SampleMatrix;
    }
    
    return null;
  }
};

// Helper function to get matrix by name or id
export const getMatrixByName = (nameOrId) => {
  // Handle undefined/null case
  if (!nameOrId) return MIL882EMatrix;

  // If input is already a matrix object, return it
  if (nameOrId.probabilities && nameOrId.severities && nameOrId.matrix) {
    return nameOrId;
  }

  // Check preset matrices
  if (nameOrId === MIL882EMatrix.name) {
    return MIL882EMatrix;
  }
  if (nameOrId === SampleMatrix.name) {
    return SampleMatrix;
  }

  // If no match found, return default matrix
  console.warn(
    `Matrix ${nameOrId} not found, using ${MIL882EMatrix.name} as fallback`
  );
  return MIL882EMatrix;
};

// Main risk calculation function (wrapper around shared implementation)
export const calculateRiskRating = calculateRating;

// Get color for risk rating
export const getRiskRatingColor = (rating, riskMatrix) => {
  if (!rating || !riskMatrix || !riskMatrix.colors) {
    return "#000000"; // Default black
  }

  return riskMatrix.colors[rating] || "#000000";
};

// Get matrix description
export const getMatrixDescription = (matrix, type, index) => {
  if (!matrix || !matrix.descriptions || !matrix.descriptions[type]) {
    return "";
  }
  return matrix.descriptions[type][index] || "";
};

// Compare two risk ratings
export const compareRiskRatings = (matrix, rating1, rating2) => {
  if (!matrix || !matrix.riskLevels) {
    return 0;
  }

  const level1 = matrix.riskLevels[rating1] || 0;
  const level2 = matrix.riskLevels[rating2] || 0;

  return level1 - level2;
};

// Validate custom matrix data
export const validateCustomMatrix = (matrixData) => {
  const errors = [];

  // Check required fields
  if (!matrixData.name) errors.push("Matrix name is required");
  if (!matrixData.probabilities?.length)
    errors.push("Probabilities are required");
  if (!matrixData.severities?.length) errors.push("Severities are required");
  if (!matrixData.matrix?.length) errors.push("Risk matrix is required");
  if (!matrixData.colors) errors.push("Color definitions are required");

  // Check matrix dimensions
  if (matrixData.matrix.length !== matrixData.probabilities.length) {
    errors.push("Matrix rows must match number of probabilities");
  }

  if (
    matrixData.matrix.some((row) => row.length !== matrixData.severities.length)
  ) {
    errors.push("Matrix columns must match number of severities");
  }

  // Check that all ratings have colors defined
  const uniqueRatings = new Set();
  matrixData.matrix.forEach((row) => {
    row.forEach((rating) => {
      if (rating) uniqueRatings.add(rating);
    });
  });

  uniqueRatings.forEach((rating) => {
    if (!matrixData.colors[rating]) {
      errors.push(`Color not defined for rating: ${rating}`);
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
};

const riskHelper = {
  getMatrixById: getMatrix,
  getMatrixByName,
  getContrastColor,
  calculateRiskRating,
};

export default riskHelper;
